'use strict'

const APPOINTMENT_BUSINESS_HOURS = Object.freeze({
  min: '08:00',
  max: '20:00'
})

const MINUTES_PER_DAY = 24 * 60
const LAST_MINUTE_OF_DAY = MINUTES_PER_DAY - 1

function toMinutes(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 && value <= MINUTES_PER_DAY
      ? value
      : null
  }

  if (typeof value !== 'string') return null

  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (minutes > 59 || hours > 24 || (hours === 24 && minutes !== 0)) return null

  return hours * 60 + minutes
}

/**
 * 指定された時間帯の開始時刻候補を、終了時刻を含めずに生成する。
 * @param {string|number} startedAt
 * @param {string|number} endedAt
 * @param {number|string} frame
 * @returns {number[]}
 */
function createTimeSlots(startedAt, endedAt, frame) {
  const start = toMinutes(startedAt)
  const end = toMinutes(endedAt)
  const frameMinutes = Number(frame)

  if (
    start === null ||
    end === null ||
    start >= end ||
    !Number.isInteger(frameMinutes) ||
    frameMinutes <= 0
  ) {
    return []
  }

  const slots = []
  for (
    let minute = start;
    minute + frameMinutes <= end;
    minute += frameMinutes
  ) {
    slots.push(minute)
  }
  return slots
}

/**
 * 時刻を指定された範囲内へ補正する。不正な入力や不正な制限は元の値を返す。
 * @param {string} value
 * @param {{min: string, max: string}|null|undefined} timeLimit
 * @returns {string}
 */
function clampTimeToLimit(value, timeLimit) {
  if (!timeLimit) return value

  const current = toMinutes(value)
  const min = toMinutes(timeLimit.min)
  const max = toMinutes(timeLimit.max)
  if (current === null || min === null || max === null || min > max)
    return value
  if (current < min) return timeLimit.min
  if (current > max) return timeLimit.max
  return value
}

function formatMinutes(value) {
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`
}

/**
 * 時刻範囲を制限内へ補正する。可能な場合は元の範囲幅を維持し、必ず開始時刻より後に終了時刻を置く。
 * @param {string} started
 * @param {string} ended
 * @param {{min: string, max: string}|null|undefined} timeLimit
 * @returns {{started: string, ended: string}}
 */
function normalizeTimeRangeToLimit(started, ended, timeLimit) {
  const start = toMinutes(started)
  const end = toMinutes(ended)

  // 時間制限がない場合も、従来どおり開始時刻と終了時刻の逆転を補正する
  if (!timeLimit) {
    if (start === null || end === null || start < end) return { started, ended }

    // ATimePickerの時刻上限は23:59のため、momentで翌日として解釈される24:00は返さない
    const normalizedEnd = Math.min(start + 60, LAST_MINUTE_OF_DAY)
    if (normalizedEnd > start) {
      return { started, ended: formatMinutes(normalizedEnd) }
    }

    // 開始時刻が23:59の場合は同日内に終了時刻を置けないため、
    // 直前の1時間へ開始時刻を戻して不正な逆転範囲を残さない。
    const normalizedStart = LAST_MINUTE_OF_DAY - 60
    return {
      started: formatMinutes(normalizedStart),
      ended: formatMinutes(LAST_MINUTE_OF_DAY)
    }
  }

  const min = toMinutes(timeLimit.min)
  const max = toMinutes(timeLimit.max)
  if (
    start === null ||
    end === null ||
    min === null ||
    max === null ||
    min > max
  ) {
    return { started, ended }
  }

  const capacity = max - min
  if (end > start) {
    const duration = Math.min(end - start, capacity)
    const normalizedStart = Math.min(Math.max(start, min), max - duration)
    return {
      started: formatMinutes(normalizedStart),
      ended: formatMinutes(normalizedStart + duration)
    }
  }

  // 逆転または同時刻の場合は、開始時刻を優先しつつ1時間（範囲が短い場合は範囲全体）を確保する
  const duration = Math.min(60, capacity)
  const normalizedStart = Math.min(Math.max(start, min), max - duration)
  return {
    started: formatMinutes(normalizedStart),
    ended: formatMinutes(normalizedStart + duration)
  }
}

function getAppointmentTimeLimit(ruleValue) {
  return ruleValue === 1 ? APPOINTMENT_BUSINESS_HOURS : null
}

/**
 * 開始・終了とも解釈できる時刻が設定された時間帯だけを残し、開始時刻の昇順に並び替える。
 * 病院側で「時間指定」を選んだのに時刻を未入力のまま保存された時間帯を除外するために使う。
 * @param {Array<{started?: string | null, ended?: string | null}>|null|undefined} times
 * @returns {Array<{started: string, ended: string}>}
 */
function filterValidTimeRanges(times) {
  if (!Array.isArray(times)) return []

  return times
    .filter((time) => {
      const start = toMinutes(time?.started)
      const end = toMinutes(time?.ended)
      return start !== null && end !== null && start < end
    })
    .sort((a, b) => toMinutes(a.started) - toMinutes(b.started))
}

module.exports = {
  APPOINTMENT_BUSINESS_HOURS,
  clampTimeToLimit,
  createTimeSlots,
  filterValidTimeRanges,
  getAppointmentTimeLimit,
  normalizeTimeRangeToLimit
}
